import {parseDwollaAPIError, DwollaErrorResponse} from "../../../util/PaymentUtils";

interface DwollaConfig {
  dwollaEnv: string;
}

interface DwollaFundingSourceLink {
  href: string;
}

interface DwollaFundingSourceResponse {
  _links: {
    'funding-source': DwollaFundingSourceLink;
  };
}

interface DwollaFundingSourcePayload {
  routingNumber: string;
  accountNumber: string;
  type: string;
  name: string;
}

export interface DwollaClient {
  configure: (dwollaEnv: string) => void;
  fundingSources: {
    create: (
      token: string,
      payload: DwollaFundingSourcePayload,
      callback: (err: DwollaErrorResponse | null, resp: DwollaFundingSourceResponse) => void
    ) => void;
  };
}

export interface CreateDwollaFundingSourceData {
  routingNumber: string;
  accountNumber: string;
  accountType: string;
  bankAccountLastFour: string;
  token: string;
}

export default class DwollaService {
  env: DwollaConfig;
  dwolla?: DwollaClient;

  constructor(env: DwollaConfig) {
    this.env = env;

    this.loadDwolla(env);
  }

  loadDwolla = (env: DwollaConfig): void => {
    if (!window.dwolla) {
      const script = document.createElement('script');
      script.onload = () => this.configureDwolla(env);
      script.src = 'https://cdn.dwolla.com/1/dwolla.min.js';
      document.head.appendChild(script);
    } else {
      this.configureDwolla(env);
    }
  }

  configureDwolla = (env: DwollaConfig): void => {
    this.dwolla = window.dwolla;
    this.dwolla?.configure(env.dwollaEnv);
  }

  createDwollaFundingSource = (
    data: CreateDwollaFundingSourceData,
    onSuccess: (dwollaFundingSourceId: string) => void,
    onFail: (message: string) => void
  ): void => {

    if (this.dwolla) {
      const {
        routingNumber,
        accountNumber,
        accountType,
        bankAccountLastFour,
        token
      } = data;

      this.dwolla.fundingSources.create(
        token,
        {
          routingNumber,
          accountNumber,
          type: accountType,
          name: bankAccountLastFour
        },
        (err, resp) => {
          if (err) {
            const status = (resp as unknown as { status?: number }).status ?? err.status;
            const messages = parseDwollaAPIError(err, status);
            onFail(messages.length ? messages[0] : "Error adding payment method.");
          } else {
            const links = resp['_links'];
            const fundingSource = links['funding-source'];
            const fundingSourceHref = fundingSource['href'];
            const dwollaFundingSourceId = fundingSourceHref.substring(fundingSourceHref.lastIndexOf("/") + 1, fundingSourceHref.length);

            onSuccess(dwollaFundingSourceId);
          }
        }
      )
    } else {
      console.warn("Attempting to create dwolla funding source without viable instance");
    }
  }
}
